import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import {
    Box,
    Typography,
    Button,
    Grid,
    Card,
    CardContent,
    CardActions,
    IconButton,
    Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ImportContactsIcon from '@mui/icons-material/ImportContacts';

import PortfolioDialog from "./PortfolioDialog";
import PortfolioDetail from "./PortfolioDetail";

//Standard Api
import axios from "axios";

//Support Safe Api Calls
import { useUser } from '../../components/UserContext';
import { createApiClient } from "../../components/useApiClient";
import { useSafeRequest } from "../../components/useSafeRequest";
import { useIsAuthenticated } from "@azure/msal-react";

import { PortfolioArtwork, PortfolioDto } from "../../models/Portfolio";

//License Check
import type { LicenseCheck } from "../../models/License";
import { useLicenseChecker } from '../../hooks/useLicenseCheck';
import LicenseExceededDialog from "../../components/LicenseManagement/LicenseExceededDialog";

export default function PortfolioManager() {

    const navigate = useNavigate();

    //License Checker
    const { canAdd } = useLicenseChecker();
    const [licenseCheck, setLicenseCheck] = useState<LicenseCheck | null>(null);
    const isTenantAdmin = true;
    const [licenseDialogOpen, setLicenseDialogOpen] = useState(false);

    const BASE_URL = process.env.REACT_APP_API_BASE_URL;


    //Support API Calls
    const [isLoading, setIsLoading] = useState(false);
    const { activeClientId, getAccessToken, user, activeClientLicense } = useUser();
    const previousClientId = useRef<number | null>(activeClientId);
    const canAddPortfolio = activeClientLicense?.canAddPortfolios ?? false;

    const clientIdRef = React.useRef<number | null>(null);
    React.useEffect(() => { clientIdRef.current = activeClientId; }, [activeClientId]);

    const isAuthenticated = useIsAuthenticated();

    // *** Setup safe API Calls ***
    const api = React.useMemo(
        () =>
            createApiClient(
                { getAccessToken, getClientId: () => clientIdRef.current },
                { timeoutMs: 12000 }
            ),
        [getAccessToken]
    );

    const [portfolios, setPortfolios] = useState<PortfolioDto[]>([]);
    const [selectedPortfolio, setSelectedPortfolio] = useState<PortfolioDto | null>(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedPortfolioId, setSelectedPortfolioId] = useState<number | null>(null);
    const [openViewDialog, setOpenViewDialog] = useState(false);

    useEffect(() => {
        loadPortfolios();

        

    }, [activeClientId]);

    //API Calls
    const portfolioReq = useSafeRequest<any[]>(api, { maxAttempts: 3 });

    const loadPortfolios = async () => {

            setIsLoading(true);

            try {
                const tasks = [
                    portfolioReq.run({ method: "GET", url: `portfolios` }),
                ];

                const [a] = await Promise.allSettled(tasks);

                
                if (a.status === "fulfilled") setPortfolios(a.value);
            }
            catch (error) {
                console.error('Failed to load portfolio data:', error);
            } finally {
                //Loading complete
                setIsLoading(false);
            }
        
    };

    const handleAddPortfolio = async () => {
        const result = await canAdd("portfolio");

        if (!result.allowed) {
            setLicenseCheck(result);
            setLicenseDialogOpen(true);
            return;
        }

        // Add New Portfolio
        setSelectedPortfolio(null);
        setOpenDialog(true);
    };

    const handleUpgrade = () => {
        setLicenseDialogOpen(false);
        navigate("/admin/tenant");
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm("Delete this portfolio?")) return;

        const accessToken = await getAccessToken();

        if (accessToken) {
            const response = await axios.delete(
                `${BASE_URL}/portfolios/${id}`,
                {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        'X-Client-Id': activeClientId?.toString() || '',
                        "Content-Type": "application/json"
                    }
                });
        }


        loadPortfolios();
    };

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6">Portfolios</Typography>
                {canAddPortfolio &&
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddPortfolio}
                    >
                        New Portfolio
                    </Button>
                }
            </Box>

            <Grid container spacing={2}>
                {portfolios.map((p) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={p.portfolioId}>
                        <Card variant="outlined">
                            <CardContent>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    {p.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" noWrap>
                                    {p.description || "No description"}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                    {p.artworkCount} artworks
                                </Typography>
                            </CardContent>
                            <CardActions sx={{ justifyContent: "flex-end" }}>
                                <Tooltip title="View">
                                    <IconButton onClick={() => { setSelectedPortfolioId(p.portfolioId); setSelectedPortfolio(p); setOpenViewDialog(true); }}>
                                        <ImportContactsIcon />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Edit">
                                    <IconButton onClick={() => { setSelectedPortfolio(p); setOpenDialog(true); }}>
                                        <EditIcon />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                    <IconButton onClick={() => handleDelete(p.portfolioId)}>
                                        <DeleteIcon />
                                    </IconButton>
                                </Tooltip>
                            </CardActions>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {openDialog && (
                <PortfolioDialog
                    open={openDialog}
                    onClose={() => { setOpenDialog(false); setSelectedPortfolio(null); }}
                    portfolio={selectedPortfolio}
                    onSaved={loadPortfolios}
                />
            )}

            {openViewDialog && (
                <PortfolioDetail
                    open={openViewDialog}
                    portfolioId={selectedPortfolioId}
                    onClose={() => {
                        setOpenViewDialog(false);
                        setSelectedPortfolioId(null);
                    }}
                    onEdit={() => {
                        setOpenViewDialog(false);
                        setOpenDialog(true);
                    }}
                />
            )}

            <LicenseExceededDialog
                open={licenseDialogOpen}
                onClose={() => setLicenseDialogOpen(false)}
                licenseCheck={licenseCheck}
                onUpgrade={handleUpgrade}
                isTenantAdmin={isTenantAdmin}
            />
        </Box>
    );
}
